<form [formGroup]="gridForm" data-testid="aggregated-data-grid-form">
  @if ({
    rows: data() | async,
    cols: columns() | async
    }; as $) {
    <!-- Table Options -->
    <div class="grid grid-nogutter mb-3" [formGroup]="applyAllForm" data-testid="table-options">
      <!-- Tax Dropdown and Apply -->
      <div class="col-12 md:col-4 lg:col-4 mb-2 md:mb-0 pr-2">
        <div class="p-inputgroup">
          <p-select formControlName="selectedTax"
            [options]="groupedTaxes()"
            [editable]="true"
            optionLabel="key"
            styleClass="w-full"
            placeholder="Select a tax"
            aria-label="select-tax-dropdown" />
          <button pButton pRipple type="button"
            [label]="'ADMIN.SUBSCRIPTION.BTN_APPLY_ALL_TAX_CODES' | translate"
            class="p-button-secondary"
            [disabled]="!applyAllForm.get('selectedTax')?.value"
            (click)="applyTaxToAll()"
            icon="fa fa-user-edit"
            aria-label="apply-tax-to-all">
          </button>
        </div>
      </div>
      <!-- Price Input and Apply -->
      <div class="col-12 md:col-4 lg:col-4 mb-2 md:mb-0 pr-2">
        <div class="p-inputgroup">
          <p-inputNumber formControlName="price"
            placeholder="Enter price"
            mode="decimal"
            [minFractionDigits]="2"
            styleClass="w-full"
            aria-label="price-input" />
          <button pButton pRipple type="button"
            [label]="'ADMIN.SUBSCRIPTION.BTN_APPLY_ALL_TAX_CODES' | translate"
            class="p-button-secondary"
            [disabled]="!applyAllForm.get('price')?.value"
            (click)="applyPriceToAll()"
            icon="fa fa-user-edit"
            aria-label="apply-price-to-all">
          </button>
        </div>
      </div>
      <!-- Tax Code Input and Apply -->
      <div class="col-12 md:col-4 lg:col-4 mb-2 md:mb-0 pr-2">
        <div class="p-inputgroup">
          <input pInputText type="text"
            formControlName="taxCode"
            class="text-sm"
            placeholder="Enter tax code"
            aria-label="tax-code-input">
            <button pButton pRipple type="button"
              [label]="'ADMIN.SUBSCRIPTION.BTN_APPLY_ALL_TAX_CODES' | translate"
              class="p-button-secondary"
              [disabled]="!applyAllForm.get('taxCode')?.value"
              (click)="applyTaxCodeToAll()"
              icon="fa fa-user-edit"
              aria-label="apply-tax-code-to-all">
            </button>
          </div>
        </div>
      </div>
      <!-- Table Options -->
      <!-- Table -->
      <p-table
        [value]="rowControls"
        [loading]="loading()"
        styleClass="p-datatable-sm p-datatable-gridlines"
        [scrollable]="true"
        scrollHeight="flex"
        responsiveLayout="stack"
        [breakpoint]="'960px'"
        formArrayName="rows"
        data-testid="aggregated-data-table">
        <!-- Header -->
        <ng-template pTemplate="header">
          <tr>
            <!-- Checkbox column in edit mode -->
            @if (isEditMode()) {
              <th style="width: 4rem" class="text-center" data-testid="checkbox-header">
                <p-tableHeaderCheckbox aria-label="select-all-checkbox" />
              </th>
            }
            @for (col of $.cols; track col.field) {
              <th
                [style.width]="col.width"
                class="text-sm font-semibold"
                [attr.data-testid]="'column-header-' + col.field">
                {{col.title}}
              </th>
            }
          </tr>
        </ng-template>
        <!-- Body -->
        <ng-template pTemplate="body" let-rowControl let-rowIndex="rowIndex">
          <tr [formGroup]="rowControl" [attr.data-testid]="'row-' + rowIndex">
            <!-- Checkbox column in edit mode -->
            @if (isEditMode() && isGroupStart(rowIndex, $.rows)) {
              <td
                [attr.rowspan]="getRowSpan(rowIndex, $.rows)"
                class="text-center"
                data-testid="checkbox-cell">
                <p-tableCheckbox [value]="rowControl.get('tenantId').value" aria-label="select-row-checkbox" />
              </td>
            }
            @if (isEditMode() && !isGroupStart(rowIndex, $.rows)) {
              <td class="hidden"></td>
            }
            <!-- Tenant Column with Rowspan -->
            @if (isGroupStart(rowIndex, $.rows)) {
              <td
                [attr.rowspan]="getRowSpan(rowIndex, $.rows)"
                class="text-sm"
                data-testid="tenant-name-cell">
                {{rowControl.get('tenantName').value}}
              </td>
            }
            @if (!isGroupStart(rowIndex, $.rows)) {
              <td class="hidden"></td>
            }
            <!-- Service Category -->
            <td class="text-sm" data-testid="service-category-cell">
              {{rowControl.get('serviceCategoryName').value}}
            </td>
            <!-- Price -->
            <td data-testid="price-cell">
              <p-inputNumber formControlName="price"
                mode="decimal"
                styleClass="w-full text-sm"
                aria-label="row-price-input" />
            </td>
            <!-- Tax -->
            <td data-testid="tax-cell">
              <div class="flex flex-column w-full">
                <p-select
                  formControlName="selectedTax"
                  [options]="rowControl.get('availableTaxes').value"
                  optionLabel="key"
                  styleClass="w-full text-sm"
                  [placeholder]="'ADMIN.SERVICES.SELECT' | translate"
                  filterBy="key"
                  [editable]="true"
                  aria-label="row-tax-dropdown">
                  @if (rowControl.get('selectedTax')?.value) {
                    <ng-template pTemplate="selectedItem">
                      <div class="text-sm">{{rowControl.get('selectedTax').value.value}}</div>
                    </ng-template>
                  }
                  <ng-template let-taxGroup pTemplate="item">
                    <div class="text-sm">{{taxGroup.key}}</div>
                  </ng-template>
                </p-select>
                @if (getCustomTaxMessage(rowControl.get('tenantId').value, rowControl.get('serviceCategoryName').value)) {
                  <small class="p-error mt-1" data-testid="tax-error-message">
                    {{getCustomTaxMessage(rowControl.get('tenantId').value, rowControl.get('serviceCategoryName').value)}}
                  </small>
                }
              </div>
            </td>
            <!-- Tax Code -->
            <td data-testid="tax-code-cell">
              <input pInputText
                type="text"
                formControlName="taxCode"
                class="w-full text-sm"
                aria-label="row-tax-code-input" />
              </td>
            </tr>
          </ng-template>
          <!-- Empty State -->
          <ng-template pTemplate="emptymessage">
            <tr>
              <td [attr.colspan]="$.cols.length" class="text-center p-4" data-testid="empty-state-message">
                No records found
              </td>
            </tr>
          </ng-template>
        </p-table>
      }
    </form>